import React from "react";
import { Box, Button, Skeleton, Typography } from "@mui/material";

import BuyButton from "../../BuyButton";
import { TaxExclusiveInfo } from "../../PriceComponents";
import { formatPrice, getProductPrice, StoreProduct } from "./data";

interface Props {
  product?: StoreProduct;
  loading: boolean;
  onRetry: () => void;
}

const BundlePurchase = ({ product, loading, onRetry }: Props) => {
  const price = getProductPrice(product);
  // Like the store tiles, the bundle's original price comes from Stripe metadata.
  const originalPrice = Number(product?.metadata?.original_price);
  const hasOriginalPrice = Number.isSafeInteger(originalPrice) && originalPrice > 0;
  const savings = price && hasOriginalPrice ? originalPrice - price.unit_amount : 0;

  if (loading) {
    return <Box role="status" aria-label="Loading bundle price" sx={{ mt: 3 }}>
      <Skeleton variant="text" width={160} height={60} sx={{ bgcolor: "rgba(255,255,255,0.12)" }} />
      <Button disabled variant="contained">Loading price…</Button>
    </Box>;
  }

  if (product?.owned) {
    return <Box sx={{ mt: 3 }}>
      <Typography sx={{ mb: 1.5 }}>You own Distant Tone Bundle.</Typography>
      <Button href="/account#licenses" variant="contained">View your licenses</Button>
    </Box>;
  }

  if (!price) {
    return <Box role="status" sx={{ mt: 3 }}>
      <Typography sx={{ fontSize: "0.875rem", mb: 1 }}>Bundle pricing is currently unavailable.</Typography>
      <Button onClick={onRetry} variant="outlined" size="small">Retry pricing</Button>
    </Box>;
  }

  return <Box sx={{ mt: 3 }}>
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
      {hasOriginalPrice && <Typography component="s" aria-label="Original price" sx={{ color: "#cbc5bf", fontSize: { xs: "1.25rem", sm: "1.75rem" }, fontWeight: 300, textDecoration: "line-through" }}>
        {formatPrice(originalPrice, price.currency)}
      </Typography>}
      <Typography component="p" sx={{ fontSize: { xs: "2rem", sm: "2.75rem" }, fontWeight: 650, color: "#fff" }}>
        {formatPrice(price.unit_amount, price.currency)}
      </Typography>
      <TaxExclusiveInfo />
    </Box>
    {savings > 0 && <Typography sx={{ color: "#ddd", fontSize: "0.875rem", mb: 2 }}>
      Save {(savings / originalPrice * 100).toFixed(0)}%
    </Typography>}
    <BuyButton product={product} text="Buy Bundle" size="large" sx={{ minWidth: "190px", borderRadius: "24px", mt: 1 }} />
  </Box>;
};

export default BundlePurchase;
